import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe, Calculator, FolderOpen, Bell, ChevronDown, ChevronUp,
  AlertTriangle, Receipt, Calendar, CheckCircle2, Circle, Plus,
  Trash2, Edit2, Save, X, Hotel, Ticket, User, FileText,
  ArrowRightLeft, Info, MapPin, Car, Shield, Wifi, CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  auto: Car,
  dokumente: Shield,
  finanzen: CreditCard,
  verkehr: AlertTriangle,
  kommunikation: Wifi,
  technik: Wifi,
  zoll: FileText,
};

const MAPPE_CATEGORIES = [
  { value: "hotel", label: "Hotel", icon: Hotel },
  { value: "ticket", label: "Ticket", icon: Ticket },
  { value: "kontakt", label: "Ansprechpartner", icon: User },
  { value: "notiz", label: "Notiz", icon: FileText },
];

// ==================== LÄNDER-CHECKLISTE ====================
export const CountryChecklist = ({ destination }) => {
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!destination || destination.length < 2) { setCountry(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/reise/features/countries/by-destination/${encodeURIComponent(destination)}`);
        if (res.data.code) setCountry(res.data);
        else setCountry(null);
      } catch { setCountry(null); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [destination]);

  if (!country) return null;

  const importantItems = country.checklist?.filter(i => i.important) || [];
  const allItems = country.checklist || [];
  const displayItems = expanded ? allItems : importantItems;

  return (
    <Card className="p-4 border-[var(--brand-border)] bg-gradient-to-r from-blue-50/50 to-white" data-testid="country-checklist">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{country.flag}</span>
          <div>
            <h4 className="font-semibold text-sm text-[var(--brand-ink)]">
              Reise-Checkliste: {country.name}
            </h4>
            <p className="text-[11px] text-[var(--brand-muted)]">
              {country.currency} | {country.language} | Notruf: {country.emergency}
            </p>
          </div>
        </div>
        <Globe className="w-4 h-4 text-[var(--brand-muted)]" />
      </div>

      <div className="space-y-1.5 mb-3">
        {displayItems.map((item, i) => {
          const Icon = CATEGORY_ICONS[item.category] || Info;
          return (
            <label key={i} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-[var(--brand-bg)] rounded px-1.5 py-1 transition">
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => setChecked(prev => ({ ...prev, [i]: !prev[i] }))}
                className="mt-0.5 rounded border-[var(--brand-border)]"
              />
              <Icon className="w-3.5 h-3.5 mt-0.5 text-[var(--brand-muted)] shrink-0" />
              <span className={`${checked[i] ? "line-through text-[var(--brand-muted)]" : "text-[var(--brand-ink)]"} ${item.important ? "font-medium" : ""}`}>
                {item.item}
              </span>
            </label>
          );
        })}
      </div>

      {allItems.length > importantItems.length && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-[var(--brand-primary)] hover:underline flex items-center gap-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Weniger anzeigen" : `Alle ${allItems.length} Punkte anzeigen`}
        </button>
      )}

      {country.tips && country.tips.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--brand-border-light)]">
          <p className="text-[11px] font-medium text-[var(--brand-muted)] mb-1.5">Tipps:</p>
          <ul className="space-y-1">
            {country.tips.map((tip, i) => (
              <li key={i} className="text-[11px] text-[var(--brand-muted)] flex items-start gap-1.5">
                <span className="text-[var(--brand-primary)]">•</span> {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[var(--brand-border-light)] grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-[var(--brand-bg)] p-2">
          <div className="text-[10px] text-[var(--brand-muted)]">Verpflegung/Tag</div>
          <div className="text-xs font-semibold">{country.verpflegung_volltag} EUR</div>
        </div>
        <div className="rounded-md bg-[var(--brand-bg)] p-2">
          <div className="text-[10px] text-[var(--brand-muted)]">Halbtag</div>
          <div className="text-xs font-semibold">{country.verpflegung_halbtag} EUR</div>
        </div>
        <div className="rounded-md bg-[var(--brand-bg)] p-2">
          <div className="text-[10px] text-[var(--brand-muted)]">km-Pauschale</div>
          <div className="text-xs font-semibold">{country.km_pauschale} EUR</div>
        </div>
      </div>
    </Card>
  );
};

// ==================== LÄNDER-EXPLORER (Standalone in Übersicht) ====================
export const CountryExplorer = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [country, setCountry] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState({});
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/reise/features/countries`);
        setCountries(res.data || []);
      } catch { setCountries([]); }
    };
    load();
  }, []);

  const selectCountry = async (code) => {
    if (!code) { setCountry(null); setSelectedCode(""); setChecked({}); return; }
    setSelectedCode(code);
    setChecked({});
    try {
      const res = await axios.get(`${API}/reise/features/countries/${code}`);
      setCountry(res.data);
    } catch { setCountry(null); }
  };

  const handleSearch = async () => {
    if (!searchText.trim()) return;
    try {
      const res = await axios.get(`${API}/reise/features/countries/by-destination/${encodeURIComponent(searchText)}`);
      if (res.data.code) { setCountry(res.data); setSelectedCode(res.data.code); setChecked({}); }
      else toast.error("Land nicht erkannt. Bitte aus der Liste waehlen.");
    } catch { toast.error("Suche fehlgeschlagen"); }
  };

  const importantItems = country?.checklist?.filter(i => i.important) || [];
  const allItems = country?.checklist || [];
  const displayItems = expanded ? allItems : importantItems;

  return (
    <Card className="p-4 border-[var(--brand-border)]" data-testid="country-explorer">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-[var(--brand-primary)]" />
        <h4 className="font-semibold text-sm text-[var(--brand-ink)]">Laenderinformationen & Einreise-Checkliste</h4>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        <div className="flex-1 min-w-[140px]">
          <Input
            placeholder="Stadt oder Land eingeben..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="h-9 text-sm"
            data-testid="country-search"
          />
        </div>
        <select
          value={selectedCode}
          onChange={(e) => selectCountry(e.target.value)}
          className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm min-w-[140px]"
          data-testid="country-select"
        >
          <option value="">Land waehlen...</option>
          {countries.map(c => (
            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
          ))}
        </select>
        <Button size="sm" onClick={handleSearch} className="h-9 text-xs bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]">
          <MapPin className="w-3 h-3 mr-1" /> Suchen
        </Button>
      </div>

      {!country && (
        <p className="text-xs text-[var(--brand-muted)] py-2">
          Waehle ein Reiseziel oder Land, um Einreisebestimmungen, Checkliste, Verpflegungspauschalen und Tipps zu sehen.
        </p>
      )}

      {country && (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[var(--brand-border-light)]">
            <span className="text-2xl">{country.flag}</span>
            <div>
              <h5 className="font-semibold text-[var(--brand-ink)]">{country.name}</h5>
              <p className="text-[11px] text-[var(--brand-muted)]">
                Waehrung: {country.currency} | Sprache: {country.language} | Notruf: {country.emergency} | Zeitzone: {country.timezone}
              </p>
            </div>
          </div>

          <div className="space-y-1.5 mb-3">
            <p className="text-xs font-medium text-[var(--brand-ink)] mb-1">Checkliste vor der Reise:</p>
            {displayItems.map((item, i) => {
              const Icon = CATEGORY_ICONS[item.category] || Info;
              return (
                <label key={i} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-[var(--brand-bg)] rounded px-1.5 py-1 transition">
                  <input
                    type="checkbox"
                    checked={!!checked[i]}
                    onChange={() => setChecked(prev => ({ ...prev, [i]: !prev[i] }))}
                    className="mt-0.5 rounded border-[var(--brand-border)]"
                  />
                  <Icon className="w-3.5 h-3.5 mt-0.5 text-[var(--brand-muted)] shrink-0" />
                  <span className={`${checked[i] ? "line-through text-[var(--brand-muted)]" : "text-[var(--brand-ink)]"} ${item.important ? "font-medium" : ""}`}>
                    {item.item}
                  </span>
                </label>
              );
            })}
          </div>

          {allItems.length > importantItems.length && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-[var(--brand-primary)] hover:underline flex items-center gap-1 mb-3">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Weniger anzeigen" : `Alle ${allItems.length} Punkte anzeigen`}
            </button>
          )}

          {country.tips && country.tips.length > 0 && (
            <div className="mb-3 p-3 rounded-lg bg-blue-50/50 border border-blue-100">
              <p className="text-[11px] font-medium text-blue-700 mb-1.5">Tipps & Hinweise:</p>
              <ul className="space-y-1">
                {country.tips.map((tip, i) => (
                  <li key={i} className="text-[11px] text-blue-600 flex items-start gap-1.5">
                    <span>•</span> {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-[var(--brand-bg)] p-2">
              <div className="text-[10px] text-[var(--brand-muted)]">Verpflegung/Tag</div>
              <div className="text-xs font-semibold">{country.verpflegung_volltag} EUR</div>
            </div>
            <div className="rounded-md bg-[var(--brand-bg)] p-2">
              <div className="text-[10px] text-[var(--brand-muted)]">Halbtag</div>
              <div className="text-xs font-semibold">{country.verpflegung_halbtag} EUR</div>
            </div>
            <div className="rounded-md bg-[var(--brand-bg)] p-2">
              <div className="text-[10px] text-[var(--brand-muted)]">km-Pauschale</div>
              <div className="text-xs font-semibold">{country.km_pauschale} EUR</div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// ==================== WÄHRUNGSRECHNER ====================
export const CurrencyConverter = () => {
  const [amount, setAmount] = useState("100");
  const [fromCurrency, setFromCurrency] = useState("CHF");
  const [toCurrency, setToCurrency] = useState("EUR");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const CURRENCIES = ["EUR", "CHF", "GBP", "USD", "PLN", "CZK", "SEK", "DKK", "NOK", "HUF"];

  const convert = useCallback(async () => {
    if (!amount || isNaN(parseFloat(amount))) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reise/features/currency/convert`, {
        amount: parseFloat(amount),
        from_currency: fromCurrency,
        to_currency: toCurrency,
      });
      setResult(res.data);
    } catch (err) {
      toast.error("Umrechnung fehlgeschlagen");
    }
    setLoading(false);
  }, [amount, fromCurrency, toCurrency]);

  useEffect(() => {
    const timer = setTimeout(() => { if (amount) convert(); }, 300);
    return () => clearTimeout(timer);
  }, [amount, fromCurrency, toCurrency, convert]);

  const swap = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  return (
    <Card className="p-4 border-[var(--brand-border)]" data-testid="currency-converter">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-[var(--brand-primary)]" />
        <h4 className="font-semibold text-sm text-[var(--brand-ink)]">Waehrungsrechner</h4>
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[80px]">
          <Label className="text-[11px]">Betrag</Label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9"
            data-testid="currency-amount"
          />
        </div>
        <div className="w-[80px]">
          <Label className="text-[11px]">Von</Label>
          <select
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
            className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm"
            data-testid="currency-from"
          >
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={swap} className="h-9 w-9 flex items-center justify-center rounded-md border border-[var(--brand-border)] hover:bg-[var(--brand-bg)] transition" title="Tauschen">
          <ArrowRightLeft className="w-3.5 h-3.5" />
        </button>
        <div className="w-[80px]">
          <Label className="text-[11px]">Nach</Label>
          <select
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
            className="w-full h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm"
            data-testid="currency-to"
          >
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {result && (
        <div className="mt-3 p-3 rounded-lg bg-[var(--brand-bg)] text-center">
          <div className="text-lg font-bold text-[var(--brand-ink)]" data-testid="currency-result">
            {result.result.toFixed(2)} {result.to_currency}
          </div>
          <div className="text-[11px] text-[var(--brand-muted)]">
            1 {result.from_currency} = {result.rate.toFixed(4)} {result.to_currency} | {result.note}
          </div>
        </div>
      )}
    </Card>
  );
};

// ==================== REISEMAPPE ====================
export const Reisemappe = ({ tripId }) => {
  const [notes, setNotes] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "notiz" });

  const loadNotes = useCallback(async () => {
    if (!tripId) return;
    try {
      const res = await axios.get(`${API}/reise/features/reisemappe/${tripId}`);
      setNotes(res.data || []);
    } catch { setNotes([]); }
  }, [tripId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const saveNote = async () => {
    if (!form.title.trim() && !form.content.trim()) return;
    try {
      if (editId) {
        await axios.put(`${API}/reise/features/reisemappe/${tripId}/notes/${editId}`, { trip_id: tripId, ...form });
        toast.success("Eintrag aktualisiert");
      } else {
        await axios.post(`${API}/reise/features/reisemappe/${tripId}/notes`, { trip_id: tripId, ...form });
        toast.success("Eintrag hinzugefuegt");
      }
      setAdding(false);
      setEditId(null);
      setForm({ title: "", content: "", category: "notiz" });
      loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/reise/features/reisemappe/${tripId}/notes/${noteId}`);
      toast.success("Eintrag geloescht");
      loadNotes();
    } catch { toast.error("Loeschen fehlgeschlagen"); }
  };

  const startEdit = (note) => {
    setForm({ title: note.title, content: note.content, category: note.category });
    setEditId(note.id);
    setAdding(true);
  };

  if (!tripId) return null;

  return (
    <Card className="p-4 border-[var(--brand-border)]" data-testid="reisemappe">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-[var(--brand-primary)]" />
          <h4 className="font-semibold text-sm text-[var(--brand-ink)]">Reisemappe</h4>
          {notes.length > 0 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-[var(--brand-bg)] text-[var(--brand-muted)]">
              {notes.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {notes.length > 0 && (
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-[var(--brand-primary)] hover:underline mr-2">
              {expanded ? "Einklappen" : "Anzeigen"}
            </button>
          )}
          <Button size="sm" variant="outline" onClick={() => { setAdding(true); setEditId(null); setForm({ title: "", content: "", category: "notiz" }); }}
            className="h-7 text-xs border-[var(--brand-border)]" data-testid="reisemappe-add">
            <Plus className="w-3 h-3 mr-1" /> Hinzufuegen
          </Button>
        </div>
      </div>

      {adding && (
        <div className="mb-3 p-3 rounded-lg border border-[var(--brand-border-light)] bg-[var(--brand-bg)]/50 space-y-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Titel (z.B. Hotel Marriott Zuerich)"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                className="h-8 text-sm"
                data-testid="reisemappe-title"
              />
            </div>
            <select
              value={form.category}
              onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
              className="h-8 rounded-md border border-[var(--brand-border)] bg-white px-2 text-xs"
            >
              {MAPPE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <textarea
            placeholder="Details, Adresse, Buchungsnummer, Notizen ..."
            value={form.content}
            onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
            rows={3}
            className="w-full rounded-md border border-[var(--brand-border)] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[var(--brand-primary)]"
            data-testid="reisemappe-content"
          />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setEditId(null); }} className="h-7 text-xs">
              <X className="w-3 h-3 mr-1" /> Abbrechen
            </Button>
            <Button size="sm" onClick={saveNote} className="h-7 text-xs bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]" data-testid="reisemappe-save">
              <Save className="w-3 h-3 mr-1" /> Speichern
            </Button>
          </div>
        </div>
      )}

      {expanded && notes.length > 0 && (
        <div className="space-y-2">
          {notes.map((note) => {
            const catInfo = MAPPE_CATEGORIES.find(c => c.value === note.category) || MAPPE_CATEGORIES[3];
            const CatIcon = catInfo.icon;
            return (
              <div key={note.id} className="flex items-start gap-2 p-2.5 rounded-lg border border-[var(--brand-border-light)] bg-white">
                <CatIcon className="w-4 h-4 mt-0.5 text-[var(--brand-muted)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--brand-ink)] truncate">{note.title || catInfo.label}</div>
                  {note.content && <p className="text-xs text-[var(--brand-muted)] mt-0.5 whitespace-pre-wrap">{note.content}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(note)} className="p-1 rounded hover:bg-[var(--brand-bg)] text-[var(--brand-muted)]">
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={() => deleteNote(note.id)} className="p-1 rounded hover:bg-red-50 text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!expanded && notes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {notes.slice(0, 4).map((note) => {
            const catInfo = MAPPE_CATEGORIES.find(c => c.value === note.category) || MAPPE_CATEGORIES[3];
            const CatIcon = catInfo.icon;
            return (
              <span key={note.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[var(--brand-bg)] text-[var(--brand-muted)]">
                <CatIcon className="w-3 h-3" /> {note.title || catInfo.label}
              </span>
            );
          })}
          {notes.length > 4 && <span className="text-[11px] text-[var(--brand-muted)] self-center">+{notes.length - 4} weitere</span>}
        </div>
      )}

      {notes.length === 0 && !adding && (
        <p className="text-xs text-[var(--brand-muted)]">
          Sammle hier alles fuer deine Reise: Hoteladresse, Tickets, Ansprechpartner, Notizen.
        </p>
      )}
    </Card>
  );
};

// ==================== ERINNERUNGEN ====================
export const TravelReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/reise/features/reminders`);
        setReminders(res.data || []);
      } catch { setReminders([]); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading || reminders.length === 0) return null;

  const ICONS = {
    alert: AlertTriangle,
    receipt: Receipt,
    calendar: Calendar,
  };
  const COLORS = {
    high: "border-red-200 bg-red-50/50 text-red-800",
    medium: "border-amber-200 bg-amber-50/50 text-amber-800",
    info: "border-blue-200 bg-blue-50/50 text-blue-800",
  };

  return (
    <Card className="p-4 border-[var(--brand-border)]" data-testid="travel-reminders">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-amber-500" />
        <h4 className="font-semibold text-sm text-[var(--brand-ink)]">Erinnerungen</h4>
        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
          {reminders.length}
        </span>
      </div>
      <div className="space-y-2">
        {reminders.map((r, i) => {
          const Icon = ICONS[r.icon] || Bell;
          const colorCls = COLORS[r.priority] || COLORS.info;
          return (
            <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${colorCls}`} data-testid={`reminder-${i}`}>
              <Icon className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
              <div className="min-w-0">
                <div className="font-medium text-sm">{r.title}</div>
                <p className="text-xs opacity-80 mt-0.5">{r.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
